import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { SettingsResponse } from '@wf/shared'
import { useServicesList } from '../prices/useServices'
import { useSettings } from '../settings/useSettings'

const HIDE_KEY = 'wf.onboard.hidden.v1'

/** Firmendaten gelten als „ausgefüllt", sobald die §14-Pflichtfelder + eine Steuer-/USt-ID vorhanden sind. */
function billingComplete(b: SettingsResponse['billing']): boolean {
  return Boolean(b && b.name && b.address && b.zip && b.city && (b.taxNumber || b.vatId))
}

/**
 * „Erste Schritte"-Checkliste (nur Abrechnungs-/Einstellungs-Rolle). Führt durch die Erstkonfiguration und
 * blendet sich selbst aus, sobald alles erledigt ist — oder dauerhaft per „Ausblenden" (localStorage).
 * Datengetrieben (Firmendaten, Leistungskatalog, erster Auftrag) — keine erfundenen Häkchen.
 */
export function Onboarding({ ordersCount }: { ordersCount: number }) {
  const settings = useSettings()
  const services = useServicesList()
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDE_KEY) === '1')

  if (hidden) return null
  // Erst urteilen, wenn die Datengrundlage da ist (sonst falsche „offen"-Markierungen beim ersten Render).
  if (!settings.data || !services.data) return null

  const steps = [
    {
      done: billingComplete(settings.data.billing),
      icon: '🧾',
      title: 'Firmendaten ausfüllen',
      sub: 'für rechtssichere Rechnungen',
      to: '/settings',
      cta: 'Ausfüllen',
    },
    {
      done: services.data.services.length > 0,
      icon: '💶',
      title: 'Erste Leistung anlegen',
      sub: 'z. B. Ölwechsel, Reifenwechsel',
      to: '/prices',
      cta: 'Zu Preise',
    },
    {
      done: ordersCount > 0,
      icon: '📋',
      title: 'Ersten Auftrag aufnehmen',
      sub: 'Kennzeichen + Aufgabe – fertig',
      to: '/new',
      cta: 'Auftrag anlegen',
    },
  ]
  const doneCount = steps.filter((s) => s.done).length
  if (doneCount === steps.length) return null

  function hide() {
    try {
      localStorage.setItem(HIDE_KEY, '1')
    } catch {
      /* Persistenz nicht verfügbar — dann bleibt die Karte halt diese Sitzung sichtbar. */
    }
    setHidden(true)
  }

  return (
    <section className="wf-onb" aria-label="Erste Schritte">
      <div className="wf-onb__head">
        <h3 className="wf-onb__title">🚀 Erste Schritte – in ein paar Minuten startklar</h3>
        <span className="wf-count">
          {doneCount}/{steps.length}
        </span>
        <button type="button" className="wf-btn wf-btn--ghost wf-btn--sm wf-onb__hide" onClick={hide}>
          Ausblenden
        </button>
      </div>
      <p className="wf-onb__sub">Hak die Punkte ab – die App führt dich. Verschwindet von selbst, wenn alles erledigt ist.</p>
      <ul className="wf-onb__list">
        {steps.map((step) => (
          <li key={step.title} className={step.done ? 'wf-onb__step wf-onb__step--done' : 'wf-onb__step'}>
            <span className="wf-onb__check" aria-hidden="true">
              {step.done ? '✓' : '○'}
            </span>
            <span className="wf-onb__stepmain">
              <span className="wf-onb__steptitle">
                {step.icon} {step.title}
              </span>
              <span className="wf-onb__stepsub">{step.sub}</span>
            </span>
            {step.done ? null : (
              <Link className="wf-btn wf-btn--ghost wf-btn--sm" to={step.to}>
                {step.cta}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
